// FieldIQ — PWA install: facility-branded manifest + add-to-home-screen state
//
// Each facility installs as its own app with its own home-screen icon, so the
// manifest has to be per-facility. A single static manifest.json can only ever
// ship the generic FieldIQ mark.
//
// The manifest and its icons are generated by the backend and proxied onto
// this origin at /pwa/. This module only points the document at them and
// tracks install state — it deliberately builds nothing locally, because
// browser-local URLs break Android installs (see applyFacilityManifest).
//
// iOS is a separate path: Safari ignores manifest icons for "Add to Home
// Screen" and reads <link rel="apple-touch-icon"> from the live DOM instead,
// so that is set here too.

const DEFAULT_ICON = "/logo-white.png";
const INSTALL_EVENT = "fieldiq:installability";
const LAST_FACILITY_KEY = "fieldiq_last_facility";

let deferredPrompt = null;
let installed = false;
let iconPreview = null;

const emit = () => window.dispatchEvent(new Event(INSTALL_EVENT));

// ── Install state ──────────────────────────────────────────────────

export const isStandalone = () => {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
};

// iPadOS 13+ reports itself as a Mac, so touch points are the tiebreaker.
export const isIOS = () => {
  const ua = navigator.userAgent || "";
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

// Every iOS browser is WebKit underneath, so none of them fire
// beforeinstallprompt and all of them install via a Share-menu item. The
// engine is the same; only the button the user has to tap differs, so this
// exists purely to name the right one in the instructions.
export const getIOSBrowser = () => {
  const ua = navigator.userAgent || "";
  if (/CriOS/i.test(ua)) return "Chrome";
  if (/EdgiOS/i.test(ua)) return "Edge";
  if (/FxiOS/i.test(ua)) return "Firefox";
  if (/OPiOS|OPT\//i.test(ua)) return "Opera";
  return "Safari";
};

export const getInstallState = () => {
  const ios = isIOS();
  return {
    standalone: isStandalone(),
    installed,
    canPrompt: !!deferredPrompt,
    ios,
    iosBrowser: ios ? getIOSBrowser() : null,
  };
};

export const getIconPreview = () => iconPreview;

export const subscribeInstallState = (cb) => {
  window.addEventListener(INSTALL_EVENT, cb);
  return () => window.removeEventListener(INSTALL_EVENT, cb);
};

// Must run before React mounts — Chrome fires beforeinstallprompt early and
// only once, and the event is useless unless it was captured.
export const initInstallCapture = () => {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    emit();
  });
};

export const promptInstall = async () => {
  if (!deferredPrompt) return "unavailable";
  const evt = deferredPrompt;
  deferredPrompt = null; // the browser allows a captured prompt exactly once
  emit();
  try {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    return outcome; // "accepted" | "dismissed"
  } catch {
    return "dismissed";
  }
};

// ── Manifest ───────────────────────────────────────────────────────

// The per-facility manifest and its icons are served by the backend and
// proxied onto this origin at /pwa/ (see fieldiq/docker/nginx.conf).
//
// They cannot be built in the browser. Installing on Android makes Chrome ask
// Google's WebAPK service to compile a real APK, and that service downloads
// the manifest and every icon from the public internet. Anything
// browser-local — a blob: manifest, data: icons — is invisible to it, so the
// build never finishes and the install hangs on "installing" forever. Real
// URLs are the whole point.
//
// They must also be same-origin: scope and start_url resolve against the
// manifest's own URL, so a cross-origin manifest would scope the app to the
// auth host and stop matching this document. That is what the proxy is for.
const manifestUrl = (facilityId) => `/pwa/${facilityId}/manifest.webmanifest`;
const iconUrl = (facilityId, spec) => `/pwa/${facilityId}/icon-${spec}.png`;

export const applyFacilityManifest = async ({ facilityId, name, logoUrl }) => {
  if (!facilityId) return false;
  const href = manifestUrl(facilityId);
  const link = document.querySelector('link[rel="manifest"]');
  const previous = link?.getAttribute("href") || "/manifest.json";

  // Point at it SYNCHRONOUSLY. This used to wait on a fetch to confirm the
  // backend would serve the facility, which left a window — a whole network
  // round trip — where the static manifest was still authoritative. Install
  // during that window and Android mints a WebAPK carrying the generic Hyve
  // mark, permanently, because the icon is baked into the APK.
  //
  // The URL is deterministic and same-origin, so guessing right is the common
  // case; the fetch below just undoes it if the facility is not served.
  if (link) link.setAttribute("href", href);

  // iOS reads this at Add-to-Home-Screen time rather than at load, so setting
  // it optimistically costs nothing and is corrected below if need be.
  let apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (!apple) {
    apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    document.head.appendChild(apple);
  }
  apple.setAttribute("sizes", "180x180");
  apple.setAttribute("href", iconUrl(facilityId, "apple"));

  const label = (name || "").trim();
  const appleTitle = document.querySelector(
    'meta[name="apple-mobile-web-app-title"]',
  );
  if (appleTitle && label) appleTitle.setAttribute("content", label);

  iconPreview = iconUrl(facilityId, "192");
  emit();

  let ok = false;
  try {
    const res = await fetch(href, { cache: "no-store" });
    ok = res.ok;
  } catch {
    ok = false;
  }
  if (!ok) {
    // Roll back rather than leave the document pointing at a 404, which would
    // take installability down with it.
    if (link) link.setAttribute("href", previous);
    apple.setAttribute("href", logoUrl || DEFAULT_ICON);
    iconPreview = logoUrl || DEFAULT_ICON;
    emit();
  }
  return ok;
};

// Applied before React mounts, so the manifest is already facility-correct
// when the browser evaluates installability. App.js re-applies with live
// branding once /my-services returns.
//
// Falls back to the last facility used on this device: the root URL carries no
// slug, and without this an install from "/" mints with the generic mark.
export const applyCachedFacilityManifest = (slug) => {
  try {
    const facilityId = slug || localStorage.getItem(LAST_FACILITY_KEY) || "";
    if (!facilityId) return;
    applyFacilityManifest({
      facilityId,
      name: localStorage.getItem(`fieldiq_name_${facilityId}`) || "",
      logoUrl: localStorage.getItem(`fieldiq_logo_${facilityId}`) || "",
    });
  } catch {}
};

export const rememberFacility = (facilityId) => {
  try {
    if (facilityId) localStorage.setItem(LAST_FACILITY_KEY, facilityId);
  } catch {}
};
